import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../model/user';
import { deleteGroupUser, getGroupUsers } from '../api/groups';
import { Program, ProgramUser } from '../model/program';
import { getProgramUsers, addUsersToProgram, removeUserFromProgram } from '../api/programs';
import { UserRole } from '../auth/jwt';
import { getRoleUsers, getUsers, grantUserRole, removeUserRole } from '../api/users';

interface UserState {
  users: User[]
  currentGroupUsers: User[]
  currentProgramUsers: ProgramUser[] | null
  currentClientAdmins: User[]
}

const initialState: UserState = {
  users: [],
  currentGroupUsers: [],
  currentProgramUsers: [],
  currentClientAdmins: []
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentGroupUsers: (state, action: PayloadAction<User[]>) => {
      state.currentGroupUsers = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.fulfilled, (state: UserState, action) => {
        state.users = action.payload;
      })
      .addCase(getUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getGroupUsersAsync.fulfilled, (state: UserState, action) => {
        state.currentGroupUsers = action.payload;
      })
      .addCase(getGroupUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeGroupUserAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.users = state.users.filter(user => user.id !== removedUserId);
        state.currentGroupUsers = state.currentGroupUsers.filter(user => user.id !== removedUserId);
        state.currentProgramUsers = state.currentProgramUsers?.filter(user => user.userId !== removedUserId) ?? state.currentProgramUsers;
      })
      .addCase(removeGroupUserAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getProgramUsersAsync.fulfilled, (state: UserState, action: PayloadAction<ProgramUser[]>) => {
        state.currentProgramUsers = action.payload;
      })
      .addCase(getProgramUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(addUsersToProgramAsync.fulfilled, (state: UserState, action: PayloadAction<ProgramUser[]>) => {
        state.currentProgramUsers = state.currentProgramUsers || [];

        let existingUserIds = new Set(state.currentProgramUsers.map(user => user.userId));
        let newProgramUsers = action.payload.filter(user => !existingUserIds.has(user.userId))
        state.currentProgramUsers.push(...newProgramUsers);
      })
      .addCase(addUsersToProgramAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeUserFromProgramAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.currentProgramUsers = state.currentProgramUsers?.filter(user => user.userId !== removedUserId) ?? null;
      })
      .addCase(removeUserFromProgramAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(getRoleUsersAsync.fulfilled, (state: UserState, action: PayloadAction<User[]>) => {
        state.currentClientAdmins = action.payload
      })
      .addCase(getRoleUsersAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(addUserRoleAsync.fulfilled, (state: UserState, action) => {
        const grantedUserId = action.meta.arg.userId;
        let user = state.users.find(user => user.id == grantedUserId)
        let alreadyAdded = state.currentClientAdmins.map(admin => admin.id).includes(grantedUserId)
        
        if(user && !alreadyAdded) {
          state.currentClientAdmins.push(user);
        }
      })
      .addCase(addUserRoleAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
      .addCase(removeUserRoleAsync.fulfilled, (state: UserState, action) => {
        const removedUserId = action.meta.arg.userId;
        state.currentClientAdmins = state.currentClientAdmins?.filter(user => user.id !== removedUserId);
      })
      .addCase(removeUserRoleAsync.rejected, (state: UserState, action) => {
        console.error(action.error.message);
      })
  },
});

export const getUsersAsync = createAsyncThunk('users/getUsersAsync', async ({ accessToken }: { accessToken: string }) => {
    const response = await getUsers(accessToken);
    return response;
  }
);

export const getGroupUsersAsync = createAsyncThunk('users/getGroupUsersAsync', async ({ accessToken, groupId }: { accessToken: string; groupId: string}) => {
    const response = await getGroupUsers(accessToken, groupId);
    return response;
  }
);

export const removeGroupUserAsync = createAsyncThunk('users/removeGroupUserAsync', async ({ accessToken, groupId, userId }: { accessToken: string; groupId: string; userId: string;}) => {
    const response = await deleteGroupUser(accessToken, groupId, userId);
    return response;
  }
);

export const getProgramUsersAsync = createAsyncThunk('program/getProgramUsersAsync', async ({ programId, accessToken }: { programId: string; accessToken: string}) => {
    const response = await getProgramUsers(accessToken, programId);
    return response;
  }
);

export const addUsersToProgramAsync = createAsyncThunk('program/addUsersToProgramAsync', async ({ users, program, accessToken }: { users: User[], program: Program; accessToken: string}) => {
    const response = await addUsersToProgram(accessToken, program, users);
    return response;
  }
);

export const removeUserFromProgramAsync = createAsyncThunk('program/removeUserFromProgramAsync', async ({ accessToken, programId, userId }: { accessToken: string; programId: string; userId: string;}) => {
    const response = await removeUserFromProgram(accessToken, programId, userId);
    return response;
  }
);

export const getRoleUsersAsync = createAsyncThunk('program/getRoleUsersAsync', async ({ accessToken, role }: { accessToken: string; role: UserRole;}) => {
    const response = await getRoleUsers(accessToken, role);
    return response;
  }
);

export const addUserRoleAsync = createAsyncThunk('program/addUserRoleAsync', async ({ accessToken, userId, role }: { accessToken: string; userId: string; role: UserRole;}) => {
    const response = await grantUserRole(accessToken, userId, role);
    return response;
  }
);

export const removeUserRoleAsync = createAsyncThunk('program/removeUserRoleAsync', async ({ accessToken, userId, role }: { accessToken: string; userId: string; role: UserRole;}) => {
    const response = await removeUserRole(accessToken, userId, role);
    return response;
  }
);

export const { setCurrentGroupUsers } = userSlice.actions;
export default userSlice.reducer;